// Which clients a person is looking at: every client (the default), or the ones they picked, by account manager or one
// by one. Saved to their account (preferences.ts syncs rnr-client-pick), so it follows them to any browser. Today, the
// business section of Main KPI, the Clients list and the client records grid all show the same pick.
import {supabase} from './supabase';

export type ClientOption={business_id:string;name:string;agency:string|null;manager_id:string|null;manager:string|null};
export type ManagerOption={manager_id:string;name:string;clients:number};
export type ClientOptions={clients:ClientOption[];managers:ManagerOption[]};
/** managers: account manager ids, or NO_MANAGER for clients nobody manages. Both empty: every client. */
export type ClientPick={managers:string[];clients:string[]};

export const PICK_KEY='rnr-client-pick';
export const NO_MANAGER='none';
export const EVERY_CLIENT:ClientPick={managers:[],clients:[]};

type Store=Pick<Storage,'getItem'|'setItem'|'removeItem'>;
const browser=():Store|null=>{try{return window.localStorage;}catch{return null;}};
const ids=(v:unknown)=>Array.isArray(v)?[...new Set(v.filter((x):x is string=>typeof x==='string'&&/^[\w-]{1,64}$/.test(x)))]:[];

export function readPick(store:Store|null=browser()):ClientPick{
 try{const v=JSON.parse(store?.getItem(PICK_KEY)||'null');return v&&typeof v==='object'?{managers:ids(v.managers),clients:ids(v.clients)}:EVERY_CLIENT;}catch{return EVERY_CLIENT;}
}
const listeners=new Set<(p:ClientPick)=>void>();
export function savePick(pick:ClientPick,store:Store|null=browser()){
 try{if(isEveryone(pick))store?.removeItem(PICK_KEY);else store?.setItem(PICK_KEY,JSON.stringify({managers:pick.managers,clients:pick.clients}));}catch{/* this browser only */}
 listeners.forEach(fn=>fn(pick));
}
export function onPick(fn:(p:ClientPick)=>void){listeners.add(fn);return()=>{listeners.delete(fn);};}

export const isEveryone=(p:ClientPick)=>!p.managers.length&&!p.clients.length;
const managedBy=(c:ClientOption,managers:string[])=>managers.includes(c.manager_id??NO_MANAGER);

/** The business ids to ask Supabase for: null for every client. Clients the person can no longer see drop out. */
export function pickedIds(pick:ClientPick,options:ClientOptions|null):string[]|null{
 if(isEveryone(pick))return null;
 if(!options)return pick.clients;
 return options.clients.filter(c=>pick.clients.includes(c.business_id)||managedBy(c,pick.managers)).map(c=>c.business_id);
}

/** Whether a client is in the pick, and whether only because their manager was picked. */
export function pickState(c:ClientOption,pick:ClientPick):'picked'|'via-manager'|'out'{
 if(managedBy(c,pick.managers))return 'via-manager';
 return pick.clients.includes(c.business_id)?'picked':'out';
}

/** "All clients", "Riley Chen", "Riley Chen and 2 more", "3 clients". */
export function pickLabel(pick:ClientPick,options:ClientOptions|null):string{
 if(isEveryone(pick))return 'All clients';
 const managerNames=pick.managers.map(id=>id===NO_MANAGER?'No manager':options?.managers.find(m=>m.manager_id===id)?.name).filter((n):n is string=>!!n);
 const extra=options?options.clients.filter(c=>pick.clients.includes(c.business_id)&&!managedBy(c,pick.managers)):pick.clients.map(id=>({business_id:id}));
 if(managerNames.length){
  const rest=managerNames.length-1+extra.length;
  return rest?`${managerNames[0]} and ${rest} more`:managerNames[0];
 }
 if(extra.length===1&&'name' in extra[0])return String(extra[0].name);
 return `${extra.length} ${extra.length===1?'client':'clients'}`;
}

/** Clients nobody manages, as a manager row the picker can offer. */
export function managerRows(options:ClientOptions):ManagerOption[]{
 const unmanaged=options.clients.filter(c=>!c.manager_id).length;
 return unmanaged?[...options.managers,{manager_id:NO_MANAGER,name:'No manager',clients:unmanaged}]:options.managers;
}

export function toggle(list:string[],id:string):string[]{return list.includes(id)?list.filter(x=>x!==id):[...list,id];}

/** Matches a client or a manager by any word of the query. */
export function matches(text:string,query:string){
 const words=query.toLowerCase().split(/\s+/).filter(Boolean);
 const hay=text.toLowerCase();
 return words.every(w=>hay.includes(w));
}

// The options load once per sign-in and are shared by every screen that shows the picker.
let cached:Promise<ClientOptions>|null=null;
export function loadClientOptions(fresh=false):Promise<ClientOptions>{
 if(!cached||fresh){
  cached=Promise.resolve(supabase.rpc('dashboard_client_options')).then(r=>{if(r.error)throw r.error;return r.data as ClientOptions;});
  cached.catch(()=>{cached=null;});
 }
 return cached;
}
export function forgetClientOptions(){cached=null;}
